import React from "react";
import { CubeIcon } from "@heroicons/react/24/outline";
import { StatusBadge } from "../../../../shared/components/badges/StatusBadge";
import TabbedForm from "../../../../shared/components/forms/FormTabs";
import PlanExpandRow from "../components/PlanExpandRow";
import { PlanService } from "../../../../shared/services/planService";
import { VaccineService } from "../../../../shared/services/vaccineService";

import apiService from "../../../../shared/services/apiService";

// /* ----------------------------------------
//    SUPPLIER OPTIONS
// ---------------------------------------- */
// export const getSupplierdetailsOptions = async () => {
//   try {
//     const res = await apiService.getAll('dropdowns/suppliers');
//     const suppliers =
//       res?.data?.suppliers ||
//       res?.data?.data?.suppliers ||
//       res?.data ||
//       [];

//     return suppliers.map(s => ({
//       value: s._id || s.id,
//       label: s.supplierName || s.name
//     }));
//   } catch {
//     return [];
//   }
// };

// /* ----------------------------------------

// 🔹 Dropdown loaders
const loadDropdownOptions = async (key) => {
  try {
    const res = await VaccineService.getAgeGroups(); // replace with generic dropdown API if needed
    const data = res?.data?.[key] || res?.data?.data?.[key] || res?.[key] || [];
    return data.map((item) => ({ value: item.value, label: item.label }));
  } catch (error) {
    console.warn(`Failed to load dropdown options for ${key}`, error);
    return [];
  }
};

// Specific dropdowns
export const getAgeGroupOptions = () => loadDropdownOptions("vaccineAgeGroups");

// Age group filter options loader (for custom filter dropdown)
const getAgeGroupFilterOptions = async () => {
  try {
    const res = await apiService.getAll("dropdowns");
    const ageGroups =
      res?.data?.vaccineAgeGroups ||
      res?.data?.data?.vaccineAgeGroups ||
      res?.vaccineAgeGroups ||
      [];
    return ageGroups.map((item) => ({
      value: item.label || item.value,
      label: item.label,
    }));
  } catch (error) {
    console.warn("Failed to load age group filter options", error);
    return [];
  }
};

let cachedVaccines = [];

export const getVaccineOptions = async () => {
  try {
    const res = await apiService.getAll("dropdowns/vaccines");
    const vaccines = res?.data?.vaccines || res?.vaccines || res?.data || [];

    cachedVaccines = vaccines.map((v) => ({
      value: v.id || v._id,
      label: v.name,
    }));

    return cachedVaccines;
  } catch {
    return [];
  }
};

/* ----------------------------------------
   CONFIG
---------------------------------------- */
export const vaccinationPlanConfig = {
  title: "Vaccination Plan",
  endpoint: "vaccine-schedules/admin",
  crudEndpoint: "vaccine-schedules",
  icon: CubeIcon,
  useUrlNavigation: true,
  basePath: "/admin/vaccination-plan",
  showStatusFilter: false,

  // Custom filters for list view
  customFilters: [
    {
      key: "ageGroupLabel",
      label: "Age Group",
      placeholder: "All Age Groups",
      optionsLoader: getAgeGroupFilterOptions,
    },
  ],

  columns: [
    {
      key: "vaccineName",
      label: "Vaccine Name",
      render: (item) => (
        <div>
          <div className="font-medium">
            {item.vaccine?.name || item.vaccineName || "—"}
          </div>
        </div>
      ),
    },
    {
      key: "ageGroupLabel",
      label: "Age Group",
      render: (item) => item.ageGroupLabel || "—",
    },
    // {
    //   key: 'status',
    //   label: 'Status',
    //   render: item => <StatusBadge status={item.status || 'ACTIVE'} />
    // }
  ],

  /* ----------------------------------------
     FORM FIELDS
  ---------------------------------------- */
  formFields: [
    {
      name: "vaccineId",
      label: "Vaccine Name",
      type: "select",
      required: true,
      optionsLoader: getVaccineOptions,
      section: "basic",
      renderValue: (v, item) => item?.vaccine?.name || item?.vaccineName || "—",
    },
    {
      name: "ageGroupLabel",
      label: "Age Group",
      type: "select",
      optionsLoader: getAgeGroupOptions,
      required: true,
      placeholder: "Select age group",
      section: "basic",
    },

    // {
    //   name: 'ageGroupLabel',
    //   label: 'Age Group',
    //   type: 'text',
    //   required: true,
    //   section: 'basic'
    // },
    {
      name: "ageInDays",
      label: "Age (Days)",
      type: "number",
      section: "basic",
      min: 0,
    },
    {
      name: "ageInMonths",
      label: "Age (Months)",
      type: "number",
      section: "basic",
      min: 0,
      step: 0.1,
    },
    {
      name: "doseNumber",
      label: "Number Of Doses",
      type: "number",
      section: "basic",
      min: 1,
    },
    {
      name: "displayOrder",
      label: "Display Order",
      type: "number",
      section: "basic",
      min: 1,
    },
    {
      name: "isRequired",
      label: "Is Required",
      type: "checkbox",
      section: "basic",
      valueProp: "checked",
      renderValue: (value) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            value
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {value ? "Yes" : "No"}
        </span>
      ),
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      section: "basic",
      rows: 3,
      fullWidth: true,
    },
  ],

  formState: { activeTab: "basic" },

  /* ----------------------------------------
     CUSTOM HANDLERS
  ---------------------------------------- */
  customHandlers: {
    // Transform flat array response from admin API
    transformData: (response) => {
      const schedules =
        response?.data?.schedules ||
        response?.data?.data?.schedules ||
        response?.schedules ||
        [];

      return schedules.map((item) => ({
        ...item,
        vaccineName: item.vaccineName || item.vaccine?.name || "—",
      }));
    },

    renderExpandedRow: (item) => <PlanExpandRow item={item} />,

    onView: async (item) => {
      const res = await PlanService.getById(item.id);
      const data = res?.data || res;

      // Handle both nested vaccine object and flat response
      if (data.vaccine && !data.vaccineId) {
        data.vaccineId = data.vaccine.id;
        data.vaccineName = data.vaccine.name;
      }

      return data;
    },

    /* ✅ STORE vaccineName when selected */
    onFieldChange: (fieldName, value, formData, setFormData, option) => {
      if (fieldName === "vaccineId" && option) {
        setFormData((prev) => ({
          ...prev,
          vaccineId: value,
          vaccineName: option.label,
        }));
        return true;
      }
      return false;
    },

    renderFormSections: (
      formFields,
      formData,
      handleFormChange,
      mode,
      renderFormField,
      activeTab,
      setActiveTab,
    ) => {
      const tabsConfig = {
        basic: { label: "Basic Info" },
      };

      return (
        <TabbedForm
          formFields={formFields}
          formData={formData}
          handleFormChange={handleFormChange}
          renderFormField={renderFormField}
          tabsConfig={tabsConfig}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      );
    },

    /* ✅ FINAL CLEAN PAYLOAD */
    processFormData: (formData) => {
      // Get vaccine name from cached options
      const selectedVaccine = cachedVaccines.find(
        (v) => v.value === formData.vaccineId,
      );

      return {
        vaccineId: formData.vaccineId,
        vaccineName: selectedVaccine?.label || formData.vaccineName || "",
        ageGroupLabel: formData.ageGroupLabel,
        ageInDays: formData.ageInDays ? Number(formData.ageInDays) : 0,
        ageInMonths: formData.ageInMonths ? Number(formData.ageInMonths) : 0,
        doseNumber: formData.doseNumber ? Number(formData.doseNumber) : 1,
        displayOrder: formData.displayOrder ? Number(formData.displayOrder) : 1,
        isRequired: Boolean(formData.isRequired),
        description: formData.description || "",
      };
    },
  },
};
